import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { Service } from '@wix/ambassador-bookings-services-v2-service/types';
import type { Event } from '@wix/ambassador-calendar-v3-event/types';
import { Schedule } from '@wix/ambassador-schedule-server/types';
import {
  GetActiveFeaturesResponse,
  GetPropertiesResponse,
} from '@wix/ambassador-services-catalog-server/types';
import {
  bookingsBookItClick,
  bookingsCantBookGroupsMessage,
  bookingsServicePageDrpodownChangeLocationSelection,
  bookingsServicesPageView,
} from '@wix/bi-logger-wixboost-ugc/v2';
import { getTrackingInfoForServicePageLoads } from '@wix/bookings-calendar-catalog-viewer-analytics-adapter';
import { isCourseService } from '@wix/bookings-calendar-catalog-viewer-mapper';
import {
  BookingsQueryParams,
  getDateRegionalSettingsLocale,
  getLanguage,
  getLocale,
  getServiceSlug,
  getUrlQueryParamValue,
  trackAnalytics,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import type { Basket } from '@wix/bookings-services-preferences-modal/types';
import { BusinessInfo, TimezoneType } from '@wix/bookings-uou-types';
import type { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { set } from 'lodash';
import { BookingsApi } from '../../../api/BookingsApi';
import { mapServiceToBookingPolicy } from '../../../mappers/booking-policy.mapper';
import { mapResponseToBusinessInfo } from '../../../mappers/bussines-info.mapper';
import { CourseAvailabilityDisplay } from '../../../service-page-view-model/details-section-view-model/detailsSectionViewModel';
import { schedulingLocationViewModelFactory } from '../../../service-page-view-model/scheduling-location-view-model/schedulingLocationViewModel';
import {
  dummySchedulingViewModel,
  SchedulingSectionStatus,
  SchedulingSectionViewModel,
  schedulingSectionViewModelFactory,
} from '../../../service-page-view-model/scheduling-section-view-model/schedulingSectionViewModel';
import {
  dummyViewModelFactory,
  ServicePageViewModel,
  servicePageViewModelFactory,
} from '../../../service-page-view-model/servicePageViewModel';
import {
  SchedulingTimezoneViewModel,
  schedulingTimezoneViewModelFactory,
} from '../../../service-page-view-model/shceduling-timezone-view-model/schedulingTimezoneViewModel';
import { CatalogSessionDto } from '../../../types/shared-types';
import { ServicePageActions } from '../actions/actions';
import { ACTION_NAMES, biDefaults, generateWidgetDefaults } from '../bi/consts';
import { SERVICE_PAGE_NAME } from '../constants';
import { BookingsServicesPageViewParams } from '../types';
import {
  handleNavigation,
  NavigateToCalendarAction,
} from './handle-navigation';
import { initUserMessage } from './init-user-message';
import { initializeViewModels } from './initialize-view-models';
import { getServiceSchedulingData } from './scheduling-fetcher';
import { createOnPreferencesModalServiceSelectedAction } from './servicesPreferencesModal/onPreferencesModalServiceSelected';
import {
  createOpenServicesPreferencesModalAction,
  OpenServicesPreferencesModalAction,
} from './servicesPreferencesModal/openServicesPreferencesModal';

export const initializeWidget = async ({
  flowAPI,
  isBookingCalendarInstalled,
  isBookingFormInstalled,
  settings,
}: {
  flowAPI: ControllerFlowAPI;
  isBookingCalendarInstalled: boolean;
  isBookingFormInstalled: boolean;
  settings: any;
}) => {
  const { reportError, experiments } = flowAPI;
  const setProps = flowAPI.controllerConfig.setProps;
  const { isViewer, isPreview, isEditor, isSSR } = flowAPI.environment;
  const { httpClient } = flowAPI;
  const bookingsApi = new BookingsApi({
    flowAPI,
  });
  const { platformAPIs, appParams, wixCodeApi } = flowAPI.controllerConfig;
  let isEditorX = false;
  const isWixStudio =
    flowAPI.controllerConfig.wixCodeApi?.location?.query?.dsOrigin === 'studio';
  let scheduleViewModel: SchedulingSectionViewModel = {
    status: SchedulingSectionStatus.LOADING,
    isBookable: false,
  };

  let viewModel: ServicePageViewModel;
  let catalogSessionsDto: CatalogSessionDto[] | undefined;
  let serviceSessions: Event[] | undefined;
  let getServiceSchedulingDataByLocation;
  let navigateToCalendar: NavigateToCalendarAction = async () => {};
  let changeTimezoneCallback: any = () => {};
  let openServicesPreferencesModal: OpenServicesPreferencesModalAction =
    async () => {};
  let onPreferencesModalServiceSelected = (...args: any[]) => {};
  let service: Service | undefined;
  let businessInfo: BusinessInfo | undefined;
  let locationViewModel;
  let hasServices = false;
  let waitingForServiceSlug = false;

  const serviceSlug = await getServiceSlug({
    wixCodeApi,
    pageName: SERVICE_PAGE_NAME,
    useCustomUrlSegments: true,
  });

  if (serviceSlug) {
    hasServices = true;
  } else if (isEditor) {
    hasServices = await bookingsApi.hasServices();
    if (hasServices) {
      waitingForServiceSlug = true;
    }
  }
    
  if (hasServices && !waitingForServiceSlug) {
    let schedule: Schedule | undefined;
    let activeFeatures: GetActiveFeaturesResponse | undefined;
    let businessProperties: GetPropertiesResponse | undefined;

    try {
      const [servicesResponse, businessInfoResponse] = await Promise.all([
        bookingsApi.queryServicesBySlug({ serviceSlug }),
        bookingsApi.getBusinessInfo().catch((e) => {
          console.error('Failed to fetch business info', e);
          return undefined;
        }),
      ]);
      service = servicesResponse;
      businessInfo = mapResponseToBusinessInfo(businessInfoResponse);
      activeFeatures = businessInfoResponse?.activeFeatures;
      businessProperties = businessInfoResponse?.businessProperties;

      if (service && isCourseService(service)) {
        schedule = await bookingsApi.listSchedule(service.id!);
      }
    } catch (e) {
      console.error('Failed to fetch service', e);
    }

    if (!service) {
      wixCodeApi.seo.setSeoStatusCode(404);
      setProps({ fitToContentHeight: true, serviceSlug });

      flowAPI.bi?.updateDefaults({
        ...biDefaults,
        ...generateWidgetDefaults(appParams, platformAPIs, isEditor),
      });

      return;
    }

    const isMultiServiceAppointmentEnabled =
      !!businessProperties?.customProperties?.some(
        ({ propertyName, value }) =>
          propertyName === 'isMultiServicesAppointmentsEnable' &&
          value === 'true',
      );

    const referralInfo = getUrlQueryParamValue(
      wixCodeApi,
      BookingsQueryParams.REFERRAL,
    );

    flowAPI.bi?.updateDefaults({
      ...biDefaults,
      ...generateWidgetDefaults(appParams, platformAPIs, isEditor),
      serviceId: service?.id,
      service_id: service?.id,
      type: service?.type,
      referralInfo,
    });

    const bookingsPolicy = mapServiceToBookingPolicy(service, schedule);

    await wixCodeApi.seo.renderSEOTags({
      itemType: ITEM_TYPES.BOOKINGS_SERVICE,
      itemData: {
        service,
        bookingsPolicy,
      },
      seoData: service.seoData,
    });

    const queryLocationId = wixCodeApi.location.query?.location;
    const selectedLocation = service.locations?.find((location) => {
      return location?.business?.id === queryLocationId;
    })
      ? queryLocationId
      : undefined;

    if (businessInfo) {
      businessInfo.language = getLanguage(wixCodeApi);
      businessInfo.regionalSettingsIntlLocale = getLocale(wixCodeApi);
      businessInfo.regionalSettingsLocale = getLocale(wixCodeApi);
      businessInfo.dateRegionalSettingsLocale =
        getDateRegionalSettingsLocale(wixCodeApi);
    }

    const initViewModels = initializeViewModels({
      service,
      businessInfo,
      bookingsPolicy,
      selectedLocation,
      isBookingCalendarInstalled,
      settings,
      flowAPI,
      isMultiServiceAppointmentEnabled,
    });
    viewModel = initViewModels.viewModel;
    locationViewModel = initViewModels.locationViewModel;

    let timezoneViewModel: SchedulingTimezoneViewModel =
      initViewModels.timezoneViewModel;

    const getActionName = async () => {
      if (isCourseService(service!)) {
        return isBookingFormInstalled
          ? ACTION_NAMES.NAVIGATE_TO_BOOKING_FORM
          : ACTION_NAMES.NAVIGATE_TO_CONTACT_FORM;
      } else {
        return isBookingCalendarInstalled
          ? ACTION_NAMES.NAVIGATE_TO_CALENDAR
          : undefined;
      }
    };

    changeTimezoneCallback = (timezoneType: TimezoneType) => {
      timezoneViewModel = schedulingTimezoneViewModelFactory({
        businessInfo,
        selectedTimezoneType: timezoneType,
        isBookingCalendarInstalled,
      });
      viewModel = servicePageViewModelFactory({
        service: service!,
        businessInfo,
        bookingsPolicy: bookingsPolicy!,
        t: flowAPI.translations.t,
        viewTimezone: timezoneViewModel.viewTimezone,
        settings,
        experiments,
        isMultiServiceAppointmentEnabled,
      });
      scheduleViewModel = schedulingSectionViewModelFactory({
        isBookable: viewModel.body.isBookable,
        catalogSessionsDto,
        serviceSessions,
        businessInfo: businessInfo!,
        viewTimezone: timezoneViewModel.viewTimezone,
        isCourse: isCourseService(service!),
        firstSessionStart: service?.schedule?.firstSessionStart?.toUTCString(),
        lastSessionEnd: service?.schedule?.lastSessionEnd?.toUTCString(),
        t: flowAPI.translations.t,
        service: service!,
        useQueryEvents,
      });

      setProps({
        viewModel,
        timezoneViewModel,
        scheduleViewModel,
      });
    };

    openServicesPreferencesModal = createOpenServicesPreferencesModalAction({
      flowAPI,
      service,
      viewModel,
      businessInfo: businessInfo!,
      isMultiServiceAppointmentEnabled,
    });

    onPreferencesModalServiceSelected =
      createOnPreferencesModalServiceSelectedAction({ flowAPI, viewModel });

    navigateToCalendar = async ({
      basket,
      initiatedBy,
    }: { initiatedBy?: string; basket?: Basket } = {}) => {
      flowAPI.bi?.report(
        bookingsBookItClick({
          section: initiatedBy,
          actionName: await getActionName(),
          businessId: undefined,
        }),
      );

      set(viewModel, 'button.navigationInitiatedBy', initiatedBy);
      setProps({ viewModel });

      return handleNavigation({
        service: service!,
        businessInfo,
        activeFeatures,
        locationId: locationViewModel.currentLocation,
        timezone: timezoneViewModel.viewTimezone,
        flowAPI,
        basket,
        onNavigationFailed: async ({ failReasons }) => {
          const { sendNotification } = await import('./sendNotification');

          sendNotification({
            appParams,
            failReasons,
            serviceV2: service,
            wixCodeApi,
          });
          flowAPI.bi?.report(
            bookingsCantBookGroupsMessage({
              widget_name: 'service_page',
              referralInfo: 'service_page',
              isPreview,
              failReason: JSON.stringify(failReasons),
            }),
          );
          showUserMessage();

          set(viewModel, 'button.navigationInitiatedBy', '');
          setProps({ viewModel });
        },
      });
    };

    const useQueryEvents = experiments.enabled(
      'specs.bookings.useQueryEventsInServicePage',
    );
    getServiceSchedulingDataByLocation = (locationId: string) => {
      locationViewModel = schedulingLocationViewModelFactory({
        service: service!,
        selectedLocation: locationId,
        t: flowAPI.translations.t,
      });
      scheduleViewModel = {
        status: SchedulingSectionStatus.LOADING,
        isBookable: viewModel.body.isBookable,
      };
      setProps({
        locationViewModel,
        scheduleViewModel,
        fitToContentHeight: true,
      });

      const handleScheduleError = (error: any) => {
        reportError(error);
        scheduleViewModel = {
          status: SchedulingSectionStatus.FAILED,
          isBookable: false,
        };
        setProps({
          scheduleViewModel,
        });
      };
      if (!businessInfo) {
        handleScheduleError(new Error('Business info is missing'));
        return;
      }
      if (!isSSR) {
        getServiceSchedulingData({
          service: service!,
          settings,
          httpClient,
          locationId,
          isIncreaseServicePageTimeBudget: true,
          timezone: businessInfo.timeZone!,
          bookingsApi,
          useQueryEvents,
        })
          .then((serviceSchedule) => {
            catalogSessionsDto =
              serviceSchedule?.sessions as CatalogSessionDto[];

            serviceSessions = serviceSchedule?.sessions as Event[];

            scheduleViewModel = schedulingSectionViewModelFactory({
              isBookable: viewModel.body.isBookable,
              catalogSessionsDto,
              businessInfo: businessInfo!,
              viewTimezone: timezoneViewModel.viewTimezone,
              isCourse: isCourseService(service!),
              firstSessionStart:
                service?.schedule?.firstSessionStart?.toUTCString(),
              lastSessionEnd: service?.schedule?.lastSessionEnd?.toUTCString(),
              t: flowAPI.translations.t,
              serviceSessions,
              service: service!,
              useQueryEvents,
            });
            setProps({
              locationViewModel,
              scheduleViewModel,
              timezoneViewModel,
            });
          })
          .catch(handleScheduleError);
      }
    };

    getServiceSchedulingDataByLocation(locationViewModel.currentLocation!);

    const trackingInfo = getTrackingInfoForServicePageLoads({
      service,
      businessName: businessInfo?.name || '',
    });

    trackAnalytics({
      wixCodeApi,
      eventId: trackingInfo.eventId as any,
      payload: trackingInfo.payload,
    });
  } else {
    isEditorX =
      flowAPI.controllerConfig.config.style.styleParams.booleans.responsive;
    const courseAvailability =
      (settings.courseAvailability &&
        (settings.displayNumberOfSpots
          ? CourseAvailabilityDisplay.NUMBER_OF_SPOTS
          : CourseAvailabilityDisplay.AVAILABILITY)) ||
      undefined;
    viewModel = dummyViewModelFactory({
      t: flowAPI.translations.t,
      isEditorX,
      isWixStudio,
      courseAvailability,
      waitingForServiceSlug,
    });
    const dummyBusinessInfo = {
      timeZone: 'UTC',
      regionalSettingsLocale: flowAPI.environment.language,
      dateRegionalSettingsLocale: flowAPI.environment.language,
    };
    scheduleViewModel = dummySchedulingViewModel({
      t: flowAPI.translations.t,
      businessInfo: dummyBusinessInfo,
      scheduleDays: settings.scheduleDays,
    });

    flowAPI.bi?.updateDefaults({
      ...biDefaults,
      ...generateWidgetDefaults(appParams, platformAPIs, isEditor),
    });
  }
  const reportPageLoaded = (params: BookingsServicesPageViewParams) =>
    flowAPI.bi?.report(bookingsServicesPageView(params));

  const changeLocationCallback = (locationGuid: string) => {
    if (!getServiceSchedulingDataByLocation) {
      return;
    }

    flowAPI.bi!.report(
      bookingsServicePageDrpodownChangeLocationSelection({
        locationGuid,
      }),
    );

    getServiceSchedulingDataByLocation(locationGuid);
  };
  const { userMessage, showUserMessage } = initUserMessage(setProps);

  const actions: ServicePageActions = {
    navigateToCalendar,
    openServicesPreferencesModal,
    onPreferencesModalServiceSelected,
    changeLocationCallback,
    reportPageLoaded,
    changeTimezoneCallback,
    closeDialog: () => {
      viewModel.dialogViewModel.isOpen = false;
      setProps({ viewModel: { ...viewModel } });
    },
  };

  setProps({
    actions,
    scheduleViewModel,
    locationViewModel,
    viewModel,
    serviceSlug,
    userMessage,
    fitToContentHeight: true,
  });
  return service;
};
